import React, { useCallback, useEffect, useMemo, useState } from "react";
import RequestV2 from "../../../api/RequestV2";
import { toast } from "react-toastify";
import Config from "../../../utils/config";
import { CsvDown } from "../../integrations/export";
import {
    PageBodyTitle,
    Card,
    Table,
    PageLoader,
    ListPageFilterBar,
    StatusBadge,
} from "../../design-system";
import { theme } from "../../design-system/theme";
import styled from "styled-components";
import { Link } from "react-router-dom";

const FILTER_INITIAL = [
    {
        name: "mobileNumber",
        type: "text",
        value: "",
        placeholder: "Mobile Number",
    },
    {
        name: "status",
        type: "select",
        value: "",
        placeholder: "Status",
        options: [
            { label: "Active", value: "ACTIVE" },
            { label: "Completed", value: "COMPLETED" },
            { label: "Upcoming", value: "UPCOMING" },
            { label: "Cancelled", value: "CANCELLED" },
            { label: "Paused", value: "PAUSED" },
        ],
    },
    {
        name: "frequency",
        type: "select",
        value: "",
        placeholder: "Frequency",
        options: [
            { label: "Daily", value: "DAILY" },
            { label: "Weekly", value: "WEEKLY" },
            { label: "Monthly", value: "MONTHLY" },
        ],
    },
];
const bp = theme.breakpoints || { sm: "640px" };
const PageWrapper = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.sm} 0 0;
`;
const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
  @media (max-width: ${bp.sm}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-left: auto;
`;

const CSV_HEADERS = [
    { label: "AutoPay No", key: "mandateNo" },
    { label: "User Name", key: "userName" },
    { label: "Mobile Number", key: "mobileNumber" },
    { label: "Status", key: "status" },
    { label: "Frequency", key: "frequency" },
    { label: "Amount / Cycle", key: "amountPerCycle" },
    { label: "Total Amount", key: "totalAmount" },
    { label: "Pending Amount", key: "pendingAmount" },
    { label: "Collected Amount", key: "collectedAmount" },
    { label: "Settled Amount", key: "settledAmount" },
    { label: "Start Date", key: "startDate" },
    { label: "End Date", key: "endDate" },
    { label: "Next Due Date", key: "nextDueDate" },
    { label: "Total Installments", key: "totalInstallments" },
];

export default function ViewMandateList() {
    const [loading, setLoading] = useState(false);
    const [controls, setControls] = useState([...FILTER_INITIAL]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [mandateList, setMandateList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [downloadPayload, setDownloadPayload] = useState({});

    const fetchList = useCallback((filterControls = controls) => {
        setLoading(true);
        const mobileNumber = filterControls.find((c) => c.name === "mobileNumber")?.value || "";
        const status = filterControls.find((c) => c.name === "status")?.value || "";
        const frequency = filterControls.find((c) => c.name === "frequency")?.value || "";
        const payload = {
            pageNo: currentPage - 1,
            pageSize,
            ...(mobileNumber && { mobileNumber }),
            ...(status && { status }),
            ...(frequency && { frequency }),
        };
        setDownloadPayload(payload);
        const api = new RequestV2(
            "",
            (res) => {
                setLoading(false);
                setMandateList(res.metaInfo.mandates);
                setTotalRecords(res.metaInfo.totalElements);
            },
            (err) => {
                setLoading(false);
                const response = err?.response?.data || err?.data || err;
                if (response?.fieldErrors?.length) {
                    response.fieldErrors.forEach((item) => {
                        toast.error(item.message);
                    });
                    return;
                }
                toast.error(response?.message || "Something went wrong");
            }
        );
        api.post(Config.apis.user.FETCH_MANDATES, payload);
    }, [currentPage, pageSize]);

    useEffect(() => {
        fetchList();
    }, [currentPage, pageSize]);

    const handleFilterChange = (name, value) => {
        setControls((prev) => prev.map((c) => (c.name === name ? { ...c, value } : c)));
    };

    const onSearch = () => {
        setCurrentPage(1);
        fetchList(controls);
    };

    const onReset = () => {
        const resetControls = FILTER_INITIAL.map((item) => ({ ...item }));
        setControls(resetControls);
        setCurrentPage(1);
        fetchList(resetControls);
    };

    const columns = [
        {
            key: "mandateNo",
            label: "AutoPay No",
            render: (_, row) => (
                <Link
                    to={`/loan-repayment-autopay/${row.mandateNo}`}
                    state={{
                        userName: row.userName,
                        mobileNumber: row.mobileNumber,
                        fromRevokeMandate: false,
                    }}
                    style={{textDecoration: "underline",}}
                >
                    {row?.mandateNo || "—"}
                </Link>
            ),
        },
        {
            key: "userName",
            label: "User Name",
            render: (_, row) => row?.userName ?? "—",
        },
        {
            key: "mobileNumber",
            label: "Mobile Number",
            render: (_, row) => row?.mobileNumber ?? "—",
        },
        {
            key: "status",
            label: "Status",
            render: (_, row) => {
                const status = row?.status;
                if (!status) return "—";
                return <StatusBadge status={status} />;
            },
        },
        {
            key: "frequency",
            label: "Frequency",
            render: (_, row) => row?.frequency ?? "—",
        },
        {
            key: "amountPerCycle",
            label: "Amount / Cycle",
            align: "right",
            render: (_, row) => row?.amountPerCycle ?? "—",
        },
        {
            key: "totalAmount",
            label: "Total Amount",
            align: "right",
            render: (_, row) => row?.totalAmount ?? "—",
        },
        {
            key: "startDate",
            label: "Start Date",
            render: (_, row) => row?.startDate ?? "—",
        },
        {
            key: "endDate",
            label: "End Date",
            render: (_, row) => row?.endDate ?? "—",
        },
        {
            key: "nextDueDate",
            label: "Next Due Date",
            render: (_, row) => row?.nextDueDate ?? "—",
        },
        {
            key: "totalInstallments",
            label: "Installments",
            align: "center",
            render: (_, row) => row?.totalInstallments ?? "—",
        },
    ];

    const filterConfig = useMemo(() => controls, [controls]);

    if (loading && mandateList.length === 0) {
        return <PageLoader />;
    }

    return (
        <PageWrapper>
            <TopBar>
                <PageBodyTitle>Loan Repayment AutoPay</PageBodyTitle>
                <ActionsRow>
                    <CsvDown
                        headers={CSV_HEADERS}
                        url={`${Config.apis.user.FETCH_MANDATES}?pageNo=0&pageSize=${totalRecords || 9999}`}
                        params={downloadPayload}
                        method="post"
                        title="CSV"
                        reportName="loan-repayment-mandates.csv"
                        useV2
                    />
                </ActionsRow>
            </TopBar>
            <Card style={{ marginTop: theme.spacing.md }}>
                <ListPageFilterBar
                    filters={filterConfig}
                    onFilterChange={handleFilterChange}
                    onSearch={onSearch}
                    onReset={onReset}
                />
                {loading ? (
                    <PageLoader />
                ) : (
                    <Table
                        data={mandateList}
                        columns={columns}
                        rowKey={(row) => row.mandateId}
                        emptyMessage="No record found"
                        pagination={{
                            currentPage,
                            pageSize,
                            total: totalRecords,
                            onPageChange: setCurrentPage,
                            pageSizeOptions: [10, 20, 50],
                            onPageSizeChange: (size) => {
                                setPageSize(size);
                                setCurrentPage(1);
                            },
                        }}
                    />
                )}
            </Card>
        </PageWrapper>
    );
}
